import { memo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { fileToBase64 } from "@/features/contracts/utils/contract.utils";
import type { NewContractData } from "../../utils/rental-create-payload";

const schema = z.object({
  contractNumber: z.string().min(1, "Número do contrato obrigatório"),
});

type FormData = z.infer<typeof schema>;

interface ContractCreateDialogProps {
  open: boolean;
  defaultValues?: NewContractData | null;
  onClose: () => void;
  onSubmit: (data: NewContractData) => void;
}

/**
 * Modal de cadastro de contrato sem chamada de API.
 *
 * Devolve `contractNumber` (obrigatório) e `contractImage` em base64
 * puro (via `fileToBase64`) ao pai. O contrato só é criado quando o
 * aluguel for finalizado — `POST /rentals` aceita criar contrato no
 * mesmo payload. Limite de upload em 5MB enforçado no front.
 */
export const ContractCreateDialog = memo(function ContractCreateDialog({
  open,
  defaultValues,
  onClose,
  onSubmit,
}: ContractCreateDialogProps) {
  const [imageBase64, setImageBase64] = useState<string | null>(
    defaultValues?.contractImage ?? null,
  );
  const [imageError, setImageError] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      contractNumber: defaultValues?.contractNumber ?? "",
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Arquivo deve ter no máximo 5MB");
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setImageBase64(base64);
      setImageError(null);
    } catch {
      setImageError("Erro ao processar arquivo");
    }
  };

  const handleSubmit = (data: FormData) => {
    onSubmit({
      contractNumber: data.contractNumber,
      ...(imageBase64 ? { contractImage: imageBase64 } : {}),
    });
    form.reset();
    setImageBase64(null);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cadastrar contrato</DialogTitle>
          <DialogDescription>
            O contrato será criado no servidor junto com o aluguel.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="contractNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número do contrato *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: 12345" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel htmlFor="contract-image">
                Imagem do contrato (opcional)
              </FormLabel>
              <label
                htmlFor="contract-image"
                className="flex items-center gap-2 rounded-md border border-dashed border-input bg-background px-3 py-4 text-sm text-muted-foreground cursor-pointer hover:bg-accent"
              >
                <Upload className="w-4 h-4" />
                <span>
                  {imageBase64 ? "Imagem anexada" : "Selecionar arquivo"}
                </span>
              </label>
              <input
                id="contract-image"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="sr-only"
              />
              {imageError ? (
                <p className="text-xs text-destructive">{imageError}</p>
              ) : null}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit">Salvar contrato</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
